import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ConfigSelect } from "@/components/config-select";
import { useLocale, type InheritedFrom } from "@/hooks/use-locale";
import { appLocales, localeNames, type Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { authErrorMessage } from "@/lib/auth-error";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from "@/hooks/use-notifications";

const LANGUAGE_OPTIONS = appLocales.map((locale) => ({
  value: locale,
  label: localeNames[locale],
}));

/** Which sentence explains an inherited language. Keyed rather than built, so `check:i18n` can
 * see all three (POS2-1161) — a key assembled from `inheritedFrom` at runtime would be invisible
 * to it and to `no-literal-string` alike. */
const INHERITED_HINT_KEYS: Record<InheritedFrom, string> = {
  organisation: "account.languageFollowingOrg",
  browser: "account.languageFollowingBrowser",
  fallback: "account.languageFollowingFallback",
};

export default function AccountProfilePage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [fullName, setFullName] = useState((user?.user_metadata?.full_name as string) ?? "");
  const [saving, setSaving] = useState(false);

  const { locale, storedLocale, inheritedFrom, setLocale, isSaving: savingLocale } = useLocale();
  const preferences = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const digestEnabled = preferences.data?.email_digest_enabled ?? true;

  function handleLanguageChange(next: string) {
    // "" is the unset option. It clears the preference rather than storing one, which is what
    // returns the user to their organisation's language (POS2-1161).
    setLocale(next === "" ? null : (next as Locale), {
      onSuccess: () =>
        toast.success(next === "" ? t("account.languageCleared") : t("account.languageUpdated")),
      onError: () => toast.error(t("account.languageError")),
    });
  }

  function handleDigestToggle(enabled: boolean) {
    updatePreferences.mutate(
      { email_digest_enabled: enabled },
      {
        onSuccess: () =>
          toast.success(enabled ? t("account.emailDigestOn") : t("account.emailDigestOff")),
        onError: () => toast.error(t("account.notificationsError")),
      },
    );
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);

    const { error } = await supabase.auth.updateUser({
      data: { full_name: fullName.trim() },
    });

    if (error) {
      toast.error(authErrorMessage(t, error));
    } else {
      toast.success(t("account.profileUpdated"));
    }
    setSaving(false);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-medium">{t("account.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("account.description")}</p>
      </div>

      <form onSubmit={handleSave} className="max-w-sm space-y-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-medium">
            {t("account.email")}
          </label>
          <Input id="email" value={user?.email ?? ""} disabled />
          <p className="text-xs text-muted-foreground">{t("account.emailHint")}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="full-name" className="text-sm font-medium">
            {t("account.fullName")}
          </label>
          <Input
            id="full-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t("account.fullNamePlaceholder")}
          />
        </div>
        {/* Saves on change rather than on Submit, like the notification switch below: the whole
            interface repaints in the new language, so the change is its own confirmation and a
            Save button beside it would imply the name field had also been sent. */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="locale" className="text-sm font-medium">
            {t("account.language")}
          </label>
          {/* Bound to the **stored** value, not the resolved one. `value={locale}` showed
              "English" for a chosen English, an inherited English, a browser English and the
              chain's final fallback alike — four states wearing one label, and no way back to
              any of the last three. */}
          <ConfigSelect
            id="locale"
            options={[{ value: "", label: t("account.languageUnset") }, ...LANGUAGE_OPTIONS]}
            value={storedLocale ?? ""}
            disabled={savingLocale}
            onChange={(e) => handleLanguageChange(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {/* The plain line also covers "the organisation is not known yet", so the hint never
                names a source it may have to take back a moment later. */}
            {storedLocale || !inheritedFrom
              ? t("account.languageHint")
              : t(INHERITED_HINT_KEYS[inheritedFrom], { language: localeNames[locale] })}
          </p>
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </form>

      <div className="max-w-sm space-y-4 border-t pt-6">
        <h2 className="text-base font-medium">{t("account.notifications")}</h2>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <label htmlFor="email-digest" className="text-sm font-medium">
              {t("account.emailDigest")}
            </label>
            <p className="text-xs text-muted-foreground">{t("account.emailDigestHint")}</p>
          </div>
          <Switch
            id="email-digest"
            checked={digestEnabled}
            disabled={preferences.isLoading || updatePreferences.isPending}
            onCheckedChange={handleDigestToggle}
          />
        </div>
      </div>
    </div>
  );
}
